import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Plus, Check, CheckCircle, X as IconX, Package, Eye, CircleDashed, ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { CustomSelect } from '../../components/CustomSelect';
import {
  catalogProductsOf, stockLocationsOf, locationLabelOf, stockRowsOf,
  emergencyRequestsOf, addEmergencyRequest, fulfillEmergencyRequest, stockTransfers, updateEmergencyRequestStatus
} from '../../data/store';
import { useSharedStore } from '../../hooks/useSharedStore';

export const EmergencyRequestsView = ({ org, accent, showNotification, lockedBranchId, zoneId }) => {
  const { version: dataVersion } = useSharedStore();
  const [activeTab, setActiveTab] = useState('all'); // all, my, overall
  const [provideStockFor, setProvideStockFor] = useState(null); // branch ID
  const [viewHistoryFor, setViewHistoryFor] = useState(null); // { branchId, filterStatus }
  const [raiseRequestOpen, setRaiseRequestOpen] = useState(false);
  const [version, setVersion] = useState(0);
  const bump = () => setVersion(v => v + 1);

  const locations = useMemo(() => stockLocationsOf(org.id), [org.id, dataVersion]);
  
  const list = useMemo(() => {
    let rows = emergencyRequestsOf(org.id);
    if (zoneId) {
       rows = rows.filter(r => r.zoneId === zoneId);
    }
    return rows;
  }, [org.id, zoneId, version, dataVersion]);

  return (
    <div className="flex flex-col gap-4 w-full relative min-h-[500px]">
      <AnimatePresence mode="wait">
        {provideStockFor ? (
          <div key="provide" className="w-full flex flex-col bg-white">
            <ProvideStockTab 
                org={org} accent={accent} 
                reqBranchId={provideStockFor} list={list} locations={locations} 
                lockedBranchId={lockedBranchId} showNotification={showNotification}
                onClose={() => { setProvideStockFor(null); }} 
                onComplete={() => { setProvideStockFor(null); bump(); }} 
            />
          </div>
        ) : raiseRequestOpen ? (
          <div key="raise" className="w-full flex flex-col bg-white">
             <RaiseRequestTab org={org} accent={accent} lockedBranchId={lockedBranchId} locations={locations} 
               onCancel={() => setRaiseRequestOpen(false)} 
               onCreate={(records) => {
                 const batchId = `batch_${Date.now()}`;
                 records.forEach(r => addEmergencyRequest({ ...r, batchId }));
                 if (showNotification) showNotification(`Broadcasted ${records.length} product request(s).`, 'success');
                 setRaiseRequestOpen(false);
                 bump();
               }} />
          </div>
        ) : (
          <div className="flex flex-col gap-4 p-6 w-full">
            <div className="flex items-center gap-6 border-b border-[var(--theme-tertiary)] overflow-x-auto scrollbar-hide shrink-0">
               {[
                 { id: 'all', label: 'All Requests' },
                 { id: 'my', label: 'My Requests' },
                 { id: 'overall', label: 'Details' }
               ].map(tab => {
                 const isActive = activeTab === tab.id;
                 return (
                   <button 
                     key={tab.id} 
                     onClick={() => { setActiveTab(tab.id); setProvideStockFor(null); setViewHistoryFor(null); }} 
                     className={`py-3 flex items-center gap-2 text-[13px] font-bold uppercase tracking-[0.1em] border-b-2 -mb-px transition-colors whitespace-nowrap ${
                       isActive
                         ? 'border-[var(--theme-primary)] text-[var(--theme-primary)]'
                         : 'border-transparent text-slate-400 hover:text-[#0d0d0d] hover:border-slate-300'
                     }`}
                     style={isActive ? { borderColor: accent, color: accent } : {}}
                   >
                     {tab.label}
                   </button>
                 );
               })}
            </div>
            


            <div className="w-full flex flex-col mt-4">
                {activeTab === 'all' && (
                  <AllRequestsTab list={list} locations={locations} lockedBranchId={lockedBranchId} onProvideStock={(bId) => { setProvideStockFor(bId); }} onViewHistory={(bId) => setViewHistoryFor({ branchId: bId, filterStatus: 'open' })} />
                )}
                {activeTab === 'my' && (
                  <MyRequestsTab 
                      list={list} 
                      lockedBranchId={lockedBranchId} 
                      locations={locations}
                      onRaiseRequest={() => setRaiseRequestOpen(true)}
                      onViewDetails={(bId, status, batchId) => setViewHistoryFor({ branchId: bId, filterStatus: status, filterBatchId: batchId })}
                      onUpdateStatus={(bId, currentStatus, newStatus, batchId) => {
                        list.filter(r => r.status === currentStatus && r.requestingBranchId === bId && (!batchId || r.batchId === batchId))
                            .forEach(r => updateEmergencyRequestStatus(r.id, newStatus));
                        bump();
                        if (showNotification) showNotification(`Requests marked as ${newStatus}.`, 'success');
                      }}
                  />
                )}
                {activeTab === 'overall' && (
                  <OverallDetailsTab org={org} lockedBranchId={lockedBranchId} />
                )}
            </div>
          </div>
        )}
      </AnimatePresence>

         <AnimatePresence>
            {viewHistoryFor && (
               <HistoryModal 
                  reqBranchId={viewHistoryFor.branchId}
                  filterStatus={viewHistoryFor.filterStatus}
                  filterBatchId={viewHistoryFor.filterBatchId}
                  org={org} 
                  onClose={() => setViewHistoryFor(null)} 
               />
            )}
         </AnimatePresence>
    </div>
  );
};

const AllRequestsTab = ({ list, locations, lockedBranchId, onProvideStock, onViewHistory }) => {
  const openRequests = list.filter(r => r.status === 'open');
  
  const grouped = useMemo(() => {
    const map = {};
    openRequests.forEach(r => {
      // Group by branch + truncated createdAt (to the second) so each raise request becomes its own card
      const batchKey = `${r.requestingBranchId}__${r.batchId || r.createdAt}`;
      if (!map[batchKey]) {
         map[batchKey] = { batchKey, branchId: r.requestingBranchId, products: new Set(), totalQty: 0, totalFulfilled: 0, createdAt: r.createdAt };
      }
      map[batchKey].products.add(r.productId);
      map[batchKey].totalQty += r.qty;
      map[batchKey].totalFulfilled += (r.fulfilledQty || 0);
    });
    return Object.values(map).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [openRequests]);

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const totalPages = Math.max(1, Math.ceil(grouped.length / perPage));
  const start = (page - 1) * perPage;
  const pageGrouped = grouped.slice(start, start + perPage);

  if (grouped.length === 0) {
    return (
      <div className="py-12 text-center text-slate-400 text-xs font-semibold uppercase tracking-widest border border-dashed rounded-lg border-[var(--theme-tertiary)] flex flex-col items-center gap-2">
        <Package size={24} className="opacity-20" />
        No active emergency requests
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar pb-12 pr-2">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <AnimatePresence>
        {pageGrouped.map(grp => {
          const loc = locations?.find(l => l.id === grp.branchId);
          const kindLabel = loc ? (loc.kind === 'warehouse' ? 'Warehouse' : 'Branch') : 'Branch';
          
          return (
          <motion.article layout initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }}
            key={grp.batchKey || grp.branchId}
            className="bg-white border rounded-lg p-5 flex flex-col gap-4 relative overflow-hidden shadow-sm hover:shadow-md transition-all"
            style={{ borderColor: 'var(--theme-tertiary)', borderTop: `3px solid ${lockedBranchId === grp.branchId ? '#10b981' : 'var(--theme-primary)'}` }}>
            
            <div className="flex flex-col gap-1">
              <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Needs Stock From({kindLabel})</span>
              <div className="flex items-center justify-between">
                <h4 className="text-lg font-bold text-[#0d0d0d]">{locationLabelOf(grp.branchId)}</h4>
                {lockedBranchId === grp.branchId && (
                  <span className="text-emerald-500 text-[10px] font-bold uppercase tracking-widest">
                    My Request
                  </span>
                )}
              </div>
            </div>
            
            <div className="mt-1 flex flex-col gap-2">
               <div className="flex justify-between items-center text-[10px] font-bold uppercase tracking-widest text-slate-500">
                 <span>{grp.totalFulfilled} Fulfilled</span>
                 <span>{grp.totalQty} Total Items</span>
               </div>
               <div className="h-2 w-full bg-rose-100 rounded-full overflow-hidden">
                 <div className="h-full bg-rose-500 rounded-full transition-all duration-500" style={{ width: `${Math.min(100, (grp.totalFulfilled / Math.max(1, grp.totalQty)) * 100)}%` }}></div>
               </div>
            </div>

            <div className="flex justify-between items-end mt-2 pt-2 border-t border-[var(--theme-tertiary)]">
               <div className="flex flex-col">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Requested</span>
                  <span className="text-[11px] font-bold text-slate-600">{new Date(grp.createdAt).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' })}</span>
               </div>
               <div className="flex items-center gap-2">
                 {lockedBranchId !== grp.branchId ? (
                   <button onClick={() => onProvideStock(grp.branchId)} 
                      className="px-5 py-2 rounded-lg bg-[#0d0d0d] text-white text-[11px] font-bold uppercase tracking-wider hover:bg-[var(--theme-primary)] transition-all shadow-md">
                      Provide
                   </button>
                 ) : (
                   <button onClick={() => onViewHistory(grp.branchId)} 
                      className="px-5 py-2 rounded-lg border border-emerald-500 text-emerald-600 text-[11px] font-bold uppercase tracking-wider hover:bg-emerald-50 transition-all">
                      View
                   </button>
                 )}
               </div>
            </div>
            </motion.article>
          );
        })}
      </AnimatePresence>
        </div>
      {grouped.length > 0 && (
        <section className="flex items-center justify-between mt-auto pt-4 border-t border-[var(--theme-tertiary)] shrink-0">
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-bold text-slate-500 uppercase tracking-widest">Showing</span>
            <CustomSelect
              value={perPage}
              onChange={(val) => {
                setPerPage(Number(val));
                setPage(1);
              }}
              options={[
                { value: 10, label: '10' },
                { value: 25, label: '25' },
                { value: 50, label: '50' },
                { value: 100, label: '100' },
                { value: grouped.length > 0 ? grouped.length : 200, label: 'All' }
              ]}
              className="w-16 bg-white border border-[var(--theme-tertiary)] rounded-sm px-2 py-1 text-[11px] font-bold text-slate-700 outline-none hover:border-[var(--theme-primary)] transition-colors cursor-pointer"
              menuUp={true}
            />
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="w-8 h-8 rounded-sm bg-white border border-[var(--theme-tertiary)] flex items-center justify-center disabled:opacity-50 hover:border-[var(--theme-primary)] transition-colors">
              <ChevronLeft size={14} />
            </button>
            <span className="px-3 py-1 rounded-sm bg-[#0d0d0d] font-bold tracking-widest text-[11px] text-white">{page} / {totalPages}</span>
            <button type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="w-8 h-8 rounded-sm bg-white border border-[var(--theme-tertiary)] flex items-center justify-center disabled:opacity-50 hover:border-[var(--theme-primary)] transition-colors">
              <ChevronRight size={14} />
            </button>
          </div>
        </section>
      )}
    </div>
  );
};

const RaiseRequestTab = ({ org, accent, lockedBranchId, locations, onCancel, onCreate }) => {
  const defaultBranch = lockedBranchId || (locations && locations.length > 0 ? locations[0].id : '');
  const [branchId, setBranchId] = useState(defaultBranch);
  const [lines, setLines] = useState([{ productId: '', variantId: '', qty: '1' }]);

  const products = useMemo(() => catalogProductsOf(org.id), [org.id]);
  const productById = useMemo(() => Object.fromEntries(products.map((p) => [p.id, p])), [products]);

  const changeProduct = (index, pid) => {
    const firstVariant = productById[pid]?.variants?.[0];
    const newLines = [...lines];
    newLines[index] = { ...newLines[index], productId: pid, variantId: firstVariant ? firstVariant.id : '' };
    setLines(newLines);
  };

  const changeLine = (index, field, value) => {
    const newLines = [...lines];
    newLines[index] = { ...newLines[index], [field]: value };
    setLines(newLines);
  };

  const addLine = () => setLines([...lines, { productId: '', variantId: '', qty: '1' }]);
  const removeLine = (index) => setLines(lines.filter((_, i) => i !== index));

  const [isSubmitting, setIsSubmitting] = useState(false);

  const canSubmit = !isSubmitting && lines.length > 0 && lines.every(l => l.productId && l.qty && l.qty > 0 && l.qty <= 500);

  const submit = async () => {
    if (!canSubmit) return;
    setIsSubmitting(true);
    try {
      const isoTime = new Date().toISOString();
      const records = lines.map(line => ({
        orgId: org.id,
        zoneId: 'zone-1',
        requestingBranchId: branchId,
        productId: line.productId,
        variantId: line.variantId,
        qty: Number(line.qty),
        status: 'open',
        createdAt: isoTime
      }));
      await onCreate(records);
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <div className="w-full h-full bg-white rounded-3xl shadow-sm border border-[var(--theme-tertiary)] overflow-hidden flex flex-col">
      <header className="px-6 py-4 border-b border-[var(--theme-tertiary)] bg-gray-50 flex items-center justify-between shrink-0">
         <div className="flex items-center gap-3">
            <button type="button" onClick={onCancel} className="p-1.5 rounded-lg text-slate-500 hover:text-[#0d0d0d] hover:bg-slate-200 transition-colors flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider">
               <ArrowLeft size={16} /> Back
            </button>
            <h2 className="text-sm font-bold text-[#0d0d0d] border-l border-slate-300 pl-3">Raise Emergency Request</h2>
         </div>
      </header>
      <div className="px-6 pt-5 pb-4 border-b border-slate-200 shrink-0">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
           <label className="block space-y-1">
             <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">Requesting Location *</span>
             <CustomSelect value={branchId} onChange={setBranchId} disabled={!!lockedBranchId}
                options={locations.map(l => ({ value: l.id, label: l.name }))}
                className="w-full bg-white border border-gray-100 rounded-lg text-xs" />
           </label>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto scrollbar-hide">
        <div className="px-6 py-5 flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <span className="text-[12px] font-extrabold text-[#0d0d0d] flex items-center gap-2"><Package size={16} style={{ color: accent }}/> Line Items ({lines.length})</span>
          </div>

          {lines.map((line, i) => {
             const selectedProduct = productById[line.productId];
             const variants = selectedProduct?.variants || [];
             return (
               <div key={i} className="rounded-lg border border-slate-200 bg-slate-50 p-5 flex flex-col gap-4 relative shadow-sm">
                 <div className="flex items-center justify-between border-b border-slate-200 pb-3">
                   <div className="flex items-center gap-6">
                     <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400 bg-white px-2 py-1 rounded shadow-sm">Item {i + 1}</span>
                   </div>
                   <button type="button" onClick={() => removeLine(i)} disabled={lines.length <= 1} className="p-1.5 rounded-full text-slate-400 hover:text-rose-500 hover:bg-rose-50 disabled:opacity-30 transition-colors">
                     <IconX size={14} />
                   </button>
                 </div>

                 <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <label className="block space-y-1">
                      <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">Product *</span>
                      <CustomSelect value={line.productId} onChange={(v) => changeProduct(i, v)}
                         options={products.map(p => ({ value: p.id, label: p.name }))}
                         className="w-full bg-white border border-gray-100 rounded-lg text-xs" />
                    </label>
                    <label className="block space-y-1">
                      <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">Variant</span>
                      <CustomSelect value={line.variantId} onChange={(v) => changeLine(i, 'variantId', v)} disabled={variants.length === 0}
                         options={variants.length ? variants.map(v => ({ value: v.id, label: v.label })) : [{ value: '', label: 'Standard' }]}
                         className="w-full bg-white border border-gray-100 rounded-lg text-xs" />
                    </label>
                    <label className="block space-y-1">
                      <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">Request Qty *</span>
                      <input type="number" min="1" value={line.qty} onChange={e => changeLine(i, 'qty', e.target.value)}
                         className="w-full bg-white border border-gray-100 rounded-lg px-3 py-2 text-xs outline-none focus:border-[var(--theme-primary)]" />
                    </label>
                 </div>
               </div>
             );
          })}

          <button type="button" onClick={addLine} 
             className="w-full py-4 border-2 border-dashed border-slate-300 rounded-lg text-slate-500 font-bold uppercase tracking-[0.14em] hover:border-[var(--theme-primary)] hover:text-[var(--theme-primary)] hover:bg-slate-50 transition-all flex items-center justify-center gap-2">
             <Plus size={16} strokeWidth={3}/> Add another product
          </button>
        </div>
      </div>

      <footer className="px-6 py-3 bg-slate-50 border-t border-[var(--theme-tertiary)] flex items-center justify-end gap-3">
         <button type="button" onClick={onCancel} className="px-5 py-2 rounded-lg text-xs font-bold uppercase tracking-wider text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 transition-colors">Cancel</button>
         <button type="button" onClick={submit} disabled={!canSubmit}
            className="px-5 py-2 rounded-lg text-xs font-bold uppercase tracking-wider text-white transition-opacity disabled:opacity-50 hover:opacity-90 flex items-center justify-center shadow-md"
            style={{ background: accent }}>
            Request
         </button>
      </footer>
    </div>
  );
};

const MyRequestsTab = ({ list, lockedBranchId, locations, onViewDetails, onUpdateStatus, onRaiseRequest }) => {
  const defaultBranch = lockedBranchId || (locations && locations.length > 0 ? locations[0].id : null);
  const [branchId, setBranchId] = useState(defaultBranch);

  const branchRequests = list.filter(r => r.requestingBranchId === branchId);

  const grouped = useMemo(() => {
    const map = {};
    branchRequests.forEach(r => {
      const key = r.batchId ? `${r.batchId}_${r.status}` : r.status;
      if (!map[key]) {
         map[key] = { key, status: r.status, batchId: r.batchId, branchId: r.requestingBranchId, products: new Set(), totalQty: 0, totalFulfilled: 0, createdAt: r.createdAt };
      }
      map[key].products.add(r.productId);
      map[key].totalQty += r.qty;
      map[key].totalFulfilled += (r.fulfilledQty || 0);
      if (new Date(r.createdAt) < new Date(map[key].createdAt)) {
         map[key].createdAt = r.createdAt;
      }
    });
    return Object.values(map).sort((a,b) => {
      if (a.status === 'open' && b.status !== 'open') return -1;
      if (b.status === 'open' && a.status !== 'open') return 1;
      return new Date(b.createdAt) - new Date(a.createdAt);
    });
  }, [branchRequests]);

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const totalPages = Math.max(1, Math.ceil(grouped.length / perPage));
  const start = (page - 1) * perPage;
  const pageGrouped = grouped.slice(start, start + perPage);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between border-b border-[var(--theme-tertiary)] pb-3 px-1 shrink-0">
         {lockedBranchId ? (
           <h2 className="text-sm font-bold uppercase tracking-widest text-[#0d0d0d]">My Branch Requests</h2>
         ) : (
           <div className="flex items-center gap-2">
             <h2 className="text-sm font-bold uppercase tracking-widest text-[#0d0d0d] mr-2">Requests For:</h2>
             <CustomSelect 
               value={branchId} 
               onChange={setBranchId}
               options={locations.map(l => ({ value: l.id, label: l.name + (l.kind === 'warehouse' ? ' (Warehouse)' : ' (Branch)') }))}
               className="w-64" 
             />
           </div>
         )}
         <button onClick={onRaiseRequest} className="px-5 py-2 rounded-lg text-xs font-bold uppercase tracking-wider text-white transition-opacity hover:opacity-90 shadow-md" style={{ background: 'var(--theme-primary)' }}>
           + Raise Request
         </button>
      </div>

      {grouped.length === 0 ? (
        <div className="py-12 text-center text-slate-400 text-xs font-semibold uppercase tracking-widest border border-dashed rounded-lg border-[var(--theme-tertiary)] flex flex-col items-center gap-2 shrink-0">
          <CircleDashed size={24} className="opacity-20" />
          No open emergency requests
        </div>
      ) : (
        <div className="overflow-y-auto custom-scrollbar pb-6 pr-2">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {pageGrouped.map(grp => {
             const statusColor = grp.status === 'open' ? 'var(--theme-primary)' : grp.status === 'completed' ? '#10b981' : '#f43f5e';
             return (
               <div key={grp.key} className="bg-white border rounded-lg p-5 flex flex-col gap-4 relative overflow-hidden shadow-sm" style={{ borderColor: 'var(--theme-tertiary)' }}>
                 <div className="absolute top-0 left-0 w-full h-1" style={{ background: statusColor }}></div>
                 <div className="flex justify-between items-center mt-1">
                   <h4 className="text-sm font-bold uppercase tracking-widest" style={{ color: statusColor }}>
                     {grp.status} Requests
                   </h4>
                   <button onClick={() => onViewDetails(grp.branchId, grp.status, grp.batchId)} className="px-3 py-1 rounded border border-slate-200 text-slate-500 hover:text-[var(--theme-primary)] hover:border-[var(--theme-primary)] text-[10px] font-bold uppercase tracking-wider transition-all">
                     View
                   </button>
                 </div>
                 
                 <div className="mt-1 flex flex-col gap-2">
                    <div className="flex justify-between items-center text-[10px] font-bold uppercase tracking-widest text-slate-500">
                      <span>{grp.totalFulfilled} Fulfilled</span>
                      <span>{grp.totalQty} Total Items</span>
                    </div>
                    <div className="h-2 w-full bg-rose-100 rounded-full overflow-hidden">
                      <div className="h-full bg-rose-500 rounded-full transition-all duration-500" style={{ width: `${Math.min(100, (grp.totalFulfilled / Math.max(1, grp.totalQty)) * 100)}%` }}></div>
                    </div>
                 </div>

                 <p className="text-[10px] text-slate-400 font-semibold uppercase tracking-widest mt-1">Requested: {new Date(grp.createdAt).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' })}</p>
                 
                 {(grp.status === 'open' || grp.status === 'fulfilled') && (
                   <div className="flex flex-col gap-2 mt-2">
                      <div className="flex gap-2">
                         {grp.status === 'open' && (
                           <button onClick={() => onUpdateStatus(grp.branchId, grp.status, 'cancelled', grp.batchId)} 
                              className="flex-1 py-2 rounded-lg border border-slate-200 text-slate-500 hover:text-rose-600 hover:border-rose-200 hover:bg-rose-50 text-xs font-bold uppercase tracking-wider transition-colors flex items-center justify-center gap-2">
                              <IconX size={14} /> Cancel
                           </button>
                         )}
                         <button onClick={() => onUpdateStatus(grp.branchId, grp.status, 'completed', grp.batchId)} 
                            className="flex-1 py-2 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-600 hover:text-white text-xs font-bold uppercase tracking-wider transition-colors flex items-center justify-center gap-2">
                            <CheckCircle size={14} /> Complete
                         </button>
                      </div>
                   </div>
                 )}
               </div>
             );
          })}
          </div>
          {grouped.length > 0 && (
            <section className="flex items-center justify-between mt-auto pt-4 border-t border-[var(--theme-tertiary)] shrink-0">
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-bold text-slate-500 uppercase tracking-widest">Showing</span>
                <CustomSelect
                  value={perPage}
                  onChange={(val) => {
                    setPerPage(Number(val));
                    setPage(1);
                  }}
                  options={[
                    { value: 10, label: '10' },
                    { value: 25, label: '25' },
                    { value: 50, label: '50' },
                    { value: 100, label: '100' },
                    { value: grouped.length > 0 ? grouped.length : 200, label: 'All' }
                  ]}
                  className="w-16 bg-white border border-[var(--theme-tertiary)] rounded-sm px-2 py-1 text-[11px] font-bold text-slate-700 outline-none hover:border-[var(--theme-primary)] transition-colors cursor-pointer"
                  menuUp={true}
                />
              </div>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="w-8 h-8 rounded-sm bg-white border border-[var(--theme-tertiary)] flex items-center justify-center disabled:opacity-50 hover:border-[var(--theme-primary)] transition-colors">
                  <ChevronLeft size={14} />
                </button>
                <span className="px-3 py-1 rounded-sm bg-[#0d0d0d] font-bold tracking-widest text-[11px] text-white">{page} / {totalPages}</span>
                <button type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="w-8 h-8 rounded-sm bg-white border border-[var(--theme-tertiary)] flex items-center justify-center disabled:opacity-50 hover:border-[var(--theme-primary)] transition-colors">
                  <ChevronRight size={14} />
                </button>
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
};


const ProvideStockTab = ({ org, accent, reqBranchId, list, locations, lockedBranchId, showNotification, onClose, onComplete }) => {
  const branchRequests = list.filter(r => r.requestingBranchId === reqBranchId && r.status === 'open');
  const products = useMemo(() => catalogProductsOf(org.id), [org.id]);
  const productById = useMemo(() => Object.fromEntries(products.map((p) => [p.id, p])), [products]);

  // Providing Branch defaults to lockedBranchId or the first available branch that isn't the requester
  const [selectedProvidingBranchId, setSelectedProvidingBranchId] = useState(
      lockedBranchId || (locations.filter(l => l.id !== reqBranchId)[0]?.id || 'main-warehouse')
  );
  const providingBranchId = lockedBranchId || selectedProvidingBranchId;

  const availableStockRows = useMemo(() => {
    if (!providingBranchId) return [];
    // Allow both pending and active stock for emergency fulfillment
    return stockRowsOf(org.id, providingBranchId).filter(r => r.status === 'pending' || r.status === 'active');
  }, [org.id, providingBranchId]);

  const [inputs, setInputs] = useState({});
  const [viewSourceFor, setViewSourceFor] = useState(null);

  const handleInput = (reqId, val) => {
    const num = parseInt(val, 10);
    setInputs(prev => ({ ...prev, [reqId]: isNaN(num) ? '' : num }));
  };

  const isMyRequest = providingBranchId === reqBranchId;

  const totalProvides = Object.values(inputs).reduce((sum, val) => sum + (Number(val) || 0), 0);
  const canSubmit = totalProvides > 0 && providingBranchId && !isMyRequest;

  const submit = () => {
    if (!canSubmit) return;
    Object.entries(inputs).forEach(([reqId, qty]) => {
       const num = Number(qty);
       if (num > 0) {
         fulfillEmergencyRequest(reqId, [{ locationId: providingBranchId, qty: num }]);
       }
    });
    if (showNotification) showNotification('Stock provided successfully!', 'success');
    onComplete();
  };

  const sourcesForReq = useMemo(() => {
     if (!viewSourceFor) return [];
     const req = list.find(r => r.id === viewSourceFor);
     if (!req || !(req.fulfilledQty > 0)) return [];
     return stockTransfers.filter(t => 
        t.toLocationId === req.requestingBranchId && 
        (t.note || '').includes(`[ReqID:${req.id}]`) &&
        (t.lines || []).some(l => l.productId === req.productId)
     );
  }, [viewSourceFor, list]);

  return (
    <div className="w-full bg-white rounded-3xl border border-[var(--theme-tertiary)] overflow-hidden flex flex-col h-full max-h-full min-h-0">
      <header className="p-6 border-b border-[var(--theme-tertiary)] flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-gray-50/50">
         <div>
           <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Details for Requesting Branch</p>
           <h2 className="text-xl font-bold text-[#0d0d0d]">{locationLabelOf(reqBranchId)}</h2>
         </div>
         <div className="flex items-center gap-3">
           {!lockedBranchId && (
              <div className="flex items-center gap-2 mr-4 border-r border-slate-200 pr-4">
                 <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Provide From:</span>
                 <CustomSelect 
                    value={selectedProvidingBranchId} 
                    onChange={setSelectedProvidingBranchId}
                    options={locations.filter(l => l.id !== reqBranchId).map(l => ({ value: l.id, label: l.name }))}
                    className="w-48 bg-white border border-[var(--theme-tertiary)] text-xs py-1.5 px-2 rounded-lg shadow-sm" 
                 />
              </div>
           )}
           {isMyRequest && (
              <div className="bg-[var(--theme-secondary)] text-[var(--theme-primary)] px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider border border-[var(--theme-primary)]">
                 Viewing Your Own Request
              </div>
           )}
           <button type="button" onClick={onClose} className="p-2 rounded-full text-slate-400 hover:text-rose-500 hover:bg-rose-50"><IconX size={16} /></button>
         </div>
      </header>

      <div className="flex-1 min-h-0 overflow-auto custom-scrollbar table-scrollbar" style={{ background: 'linear-gradient(to bottom, var(--theme-secondary) 0px, var(--theme-secondary) 38px, var(--theme-tertiary) 38px, var(--theme-tertiary) 39px, white 39px, white 100%)' }}>
        <table className="w-full min-w-[760px] text-left border-collapse table-fixed">
          <thead className="sticky top-0 z-10 shadow-sm ring-1 ring-[var(--theme-tertiary)]">
            <tr className="bg-gray-50/80 text-slate-500 border-b border-[var(--theme-tertiary)]">
              <th className="px-3 py-3 border-r border-[var(--theme-tertiary)] font-bold text-[10px] uppercase tracking-[0.12em]">Product Name</th>
              <th className="px-3 py-3 border-r border-[var(--theme-tertiary)] font-bold w-32 text-[10px] uppercase tracking-[0.12em]">Item Barcode</th>
              <th className="px-2 py-3 border-r border-[var(--theme-tertiary)] font-bold w-24 text-[10px] uppercase tracking-[0.12em]">Variant</th>
              <th className="px-2 py-3 border-r border-[var(--theme-tertiary)] font-bold w-24 text-center text-[10px] uppercase tracking-[0.12em] whitespace-nowrap">Raised Qty</th>
              <th className="px-3 py-3 border-r border-[var(--theme-tertiary)] font-bold w-24 text-[10px] uppercase tracking-[0.12em]">Status %</th>
              <th className="px-2 py-3 border-r border-[var(--theme-tertiary)] font-bold w-24 text-center text-[10px] uppercase tracking-[0.12em]" title="Stock already in Pending tab ready to provide">Available</th>
              <th className="px-2 py-3 border-r border-[var(--theme-tertiary)] font-bold w-24 text-center text-[10px] uppercase tracking-[0.12em]" title="Active stock on shelf that can be moved to pending">Reserved</th>
              <th className="px-2 py-3 border-r border-[var(--theme-tertiary)] font-bold w-32 text-center text-[10px] uppercase tracking-[0.12em]">Provide Qty</th>
              <th className="px-2 py-3 w-16 text-center text-[10px] uppercase tracking-[0.12em]">Actions</th>
            </tr>
          </thead>
          <tbody className="text-slate-700 font-medium">
            {branchRequests.map(req => {
               const prod = productById[req.productId];
               const varLabel = (prod?.variants || []).find(v => v.id === req.variantId)?.label || 'Standard';
               const fulfilled = req.fulfilledQty || 0;
               const remaining = Math.max(0, req.qty - fulfilled);
               const stockRows = availableStockRows.filter(r => r.productId === req.productId && (r.variantId || '') === (req.variantId || ''));
               const activeStock = stockRows.filter(r => r.status === 'active').reduce((sum, r) => sum + r.qty, 0);
               
               // ALL pending stock can be provided for emergencies, regardless of the note
               const pendingStock = stockRows.filter(r => r.status === 'pending').reduce((sum, r) => sum + r.qty, 0);
               
               const maxProvide = Math.min(remaining, pendingStock);
               
               const inputVal = inputs[req.id] || '';
               const currentProvide = Number(inputVal) || 0;
               const simulatedFulfilled = Math.min(req.qty, fulfilled + currentProvide);
               
               const pct = Math.round((simulatedFulfilled / req.qty) * 100);
               let pctColor = 'text-rose-600';
               if (pct >= 100) { pctColor = 'text-emerald-700'; }
               else if (pct >= 50) { pctColor = 'text-amber-700'; }

               const isError = Number(inputVal) > maxProvide;
               const isViewing = viewSourceFor === req.id;

               return (
                 <React.Fragment key={req.id}>
                 <tr className={`border-b border-slate-200 transition-colors group ${isViewing ? 'bg-slate-50' : 'hover:bg-slate-50/50'}`}>
                    <td className="px-3 py-2.5 border-r border-slate-200 font-bold text-slate-800 text-xs truncate">{prod?.name || req.productId}</td>
                    <td className="px-3 py-2.5 border-r border-slate-200 text-slate-500 text-[10px] font-mono truncate uppercase">{prod?.barcode || req.productId}</td>
                    <td className="px-2 py-2.5 border-r border-slate-200 text-slate-500 text-xs font-semibold truncate">{varLabel}</td>
                    <td className="px-2 py-2.5 border-r border-slate-200 text-center font-bold text-slate-800 text-xs">{req.qty}</td>
                    <td className="px-3 py-2.5 border-r border-slate-200">
                       <div className="flex justify-center items-center h-full w-full">
                          <span className={`text-[11px] font-bold tracking-widest ${pctColor}`}>{pct}%</span>
                       </div>
                    </td>
                    <td className="px-2 py-2.5 border-r border-slate-200 text-center bg-emerald-50/30">
                       <span className={`text-xs font-bold ${pendingStock > 0 ? 'text-emerald-600' : 'text-slate-400'}`}>{pendingStock}</span>
                    </td>
                    <td className="px-2 py-2.5 border-r border-slate-200 text-center bg-amber-50/30">
                       <span className={`text-xs font-bold ${activeStock > 0 ? 'text-amber-600' : 'text-slate-400'}`}>{activeStock}</span>
                    </td>
                    <td className="px-2 py-1.5 border-r border-slate-200 text-center relative group/input">
                       <input type="number" min="0" max={maxProvide} value={inputVal} onChange={e => handleInput(req.id, e.target.value)} disabled={isMyRequest || maxProvide === 0}
                         placeholder="0"
                         className={`w-16 mx-auto text-center rounded bg-slate-50 border ${isError ? 'border-rose-400 bg-rose-50 text-rose-600' : 'border-[var(--theme-tertiary)] focus:bg-white focus:border-[var(--theme-primary)]'} outline-none px-1 py-1 font-bold text-xs disabled:opacity-40 transition-all`} />
                       {isError && (
                         <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 whitespace-nowrap bg-gray-900 text-white text-[10px] px-2 py-1 rounded opacity-0 invisible group-hover/input:opacity-100 group-hover/input:visible transition-all z-20">
                           Max {maxProvide}
                         </div>
                       )}
                    </td>
                    <td className="px-2 py-2.5 text-center">
                       <button type="button" onClick={() => setViewSourceFor(isViewing ? null : req.id)}
                          className={`p-1 rounded transition-colors inline-flex items-center justify-center ${isViewing ? 'bg-[var(--theme-primary)] text-white' : 'text-slate-400 hover:text-[var(--theme-primary)] hover:bg-slate-100'}`} title="View sources">
                          <Eye size={14} />
                       </button>
                    </td>
                 </tr>
                 <AnimatePresence>
                   {isViewing && (
                     <tr>
                       <td colSpan="7" className="p-0 border-0">
                         <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="bg-slate-50 border-t border-[var(--theme-tertiary)] p-4 text-xs overflow-hidden shadow-inner">
                            <h5 className="font-bold uppercase tracking-widest text-slate-500 mb-3 ml-2">Fulfillment Sources</h5>
                            {sourcesForReq.length === 0 ? (
                              <p className="text-slate-400 font-semibold italic ml-2">No fulfillments tracked yet.</p>
                            ) : (
                              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 ml-2">
                                {sourcesForReq.map((src, idx) => {
                                   const qty = src.lines[0]?.qty || 0;
                                   return (
                                     <div key={idx} className="bg-white p-3 rounded-lg border border-[var(--theme-tertiary)] flex items-center justify-between shadow-sm">
                                       <div className="flex flex-col">
                                         <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Provided By</span>
                                         <span className="font-bold text-[var(--theme-primary)] text-sm">{locationLabelOf(src.fromLocationId)}</span>
                                       </div>
                                       <div className="flex flex-col items-end">
                                         <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Quantity</span>
                                         <span className="font-bold text-emerald-600 text-sm">+{qty}</span>
                                       </div>
                                     </div>
                                   )
                                })}
                              </div>
                            )}
                         </motion.div>
                       </td>
                     </tr>
                   )}
                 </AnimatePresence>
                 </React.Fragment>
               );
            })}
          </tbody>
        </table>
      </div>

      <footer className="p-6 border-t border-[var(--theme-tertiary)] bg-gray-50 flex items-center justify-between">
         <p className="text-[11px] font-semibold text-slate-500">
           {isMyRequest ? "You cannot fulfill your own request." : "Enter quantity to provide and confirm."}
         </p>
         <div className="flex gap-3">
           <button type="button" onClick={onClose} className="px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 hover:text-slate-700">Cancel</button>
           <button type="button" onClick={submit} disabled={!canSubmit}
              className="inline-flex items-center gap-2 px-8 py-3 rounded-lg text-white text-xs font-bold uppercase tracking-wider transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
              style={{ background: canSubmit ? accent : '#cbd5e1' }}>
              Provide
           </button>
         </div>
      </footer>
    </div>
  );
};

EmergencyRequestsView.propTypes = {
  org: PropTypes.object.isRequired,
  accent: PropTypes.string.isRequired,
  showNotification: PropTypes.func,
  lockedBranchId: PropTypes.string,
  zoneId: PropTypes.string
};
AllRequestsTab.propTypes = { list: PropTypes.array.isRequired, onProvideStock: PropTypes.func.isRequired };
RaiseRequestTab.propTypes = { org: PropTypes.object.isRequired, accent: PropTypes.string.isRequired, lockedBranchId: PropTypes.string, locations: PropTypes.array.isRequired, onCancel: PropTypes.func.isRequired, onCreate: PropTypes.func.isRequired };
MyRequestsTab.propTypes = { list: PropTypes.array.isRequired, lockedBranchId: PropTypes.string, locations: PropTypes.array.isRequired, onViewDetails: PropTypes.func.isRequired };
ProvideStockTab.propTypes = { org: PropTypes.object.isRequired, accent: PropTypes.string.isRequired, reqBranchId: PropTypes.string.isRequired, list: PropTypes.array.isRequired, locations: PropTypes.array.isRequired, lockedBranchId: PropTypes.string, showNotification: PropTypes.func, onClose: PropTypes.func.isRequired, onComplete: PropTypes.func.isRequired };


const HistoryModal = ({ org, reqBranchId, filterStatus, filterBatchId, onClose }) => {
  const { version } = useSharedStore();
  const allRequests = useMemo(() => emergencyRequestsOf(org.id), [org.id, version]);
  let branchHistory = allRequests.filter(r => r.requestingBranchId === reqBranchId);
  if (filterStatus) branchHistory = branchHistory.filter(r => r.status === filterStatus);
  if (filterBatchId) branchHistory = branchHistory.filter(r => r.batchId === filterBatchId);
  const products = useMemo(() => catalogProductsOf(org.id), [org.id]);
  const productById = useMemo(() => Object.fromEntries(products.map((p) => [p.id, p])), [products]);

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[120] flex items-center justify-center p-4"
        style={{ background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(6px)' }}>
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
          className="w-full max-w-4xl bg-white rounded-3xl shadow-2xl border border-[var(--theme-tertiary)] overflow-hidden flex flex-col"
          style={{ maxHeight: '90vh' }}>
          <header className="px-6 py-4 border-b border-[var(--theme-tertiary)] flex items-center justify-between bg-gray-50/50">
            <p className="text-sm font-bold uppercase tracking-[0.16em] text-[var(--theme-primary)]">{locationLabelOf(reqBranchId)}</p>
            <button type="button" onClick={onClose} className="p-2 rounded-full text-slate-400 hover:text-rose-500 hover:bg-rose-50"><IconX size={16} /></button>
          </header>
          
          <div className="flex-1 overflow-auto custom-scrollbar">
            <div className="p-6">
            {branchHistory.length === 0 ? (
              <p className="text-center text-slate-400 text-sm font-semibold italic py-8">No requests found for this branch.</p>
            ) : (
              <div className="overflow-auto rounded-lg border border-[var(--theme-tertiary)]">
                <table className="w-full text-left border-collapse min-w-[600px]">
                  <thead className="bg-slate-50 sticky top-0 z-10">
                    <tr className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-[var(--theme-tertiary)]">
                      <th className="p-3 font-bold border-r border-[var(--theme-tertiary)]">Date &amp; Time</th>
                      <th className="p-3 font-bold border-r border-[var(--theme-tertiary)]">Product</th>
                      <th className="p-3 font-bold border-r border-[var(--theme-tertiary)]">Variant</th>
                      <th className="p-3 font-bold border-r border-[var(--theme-tertiary)]">Requested Qty</th>
                      <th className="p-3 font-bold border-r border-[var(--theme-tertiary)]">Fulfilled</th>
                      <th className="p-3 font-bold text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--theme-tertiary)] text-sm">
                    {branchHistory.sort((a,b) => new Date(b.createdAt) - new Date(a.createdAt)).map(req => {
                      const prod = productById[req.productId];
                      const varLabel = (prod?.variants || []).find(v => v.id === req.variantId)?.label || 'Standard';
                      return (
                        <tr key={req.id} className="hover:bg-slate-50/50 transition-colors">
                          <td className="p-3 text-slate-500 font-medium text-xs whitespace-nowrap border-r border-[var(--theme-tertiary)]">{new Date(req.createdAt).toLocaleString()}</td>
                          <td className="p-3 font-bold text-slate-700 border-r border-[var(--theme-tertiary)] text-xs">{prod?.name || req.productId}</td>
                          <td className="p-3 text-slate-500 border-r border-[var(--theme-tertiary)] text-xs">{varLabel}</td>
                          <td className="p-3 font-bold text-[#0d0d0d] text-xs text-center border-r border-[var(--theme-tertiary)]">{req.qty}</td>
                          <td className="p-3 font-bold text-emerald-600 text-xs text-center border-r border-[var(--theme-tertiary)]">{req.fulfilledQty || 0}</td>
                          <td className={`p-3 text-center text-[10px] font-bold uppercase tracking-wider ${req.status === 'open' ? 'text-rose-600' : 'text-emerald-600'}`}>
                              {req.status}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};


const OverallDetailsTab = ({ org, lockedBranchId }) => {
  const { version } = useSharedStore();
  const products = useMemo(() => catalogProductsOf(org.id), [org.id, version]);
  const productById = useMemo(() => Object.fromEntries(products.map((p) => [p.id, p])), [products]);

  const emergencyFulfillments = useMemo(() => {
     let filtered = stockTransfers.filter(t => t.note && t.note.toLowerCase().includes('emergency'));
     if (lockedBranchId) {
        filtered = filtered.filter(t => t.fromLocationId === lockedBranchId || t.toLocationId === lockedBranchId);
     }
     return filtered;
  }, [version, lockedBranchId]);

  const flattenedFulfillments = useMemo(() => {
     const flat = [];
     emergencyFulfillments.forEach(t => {
        (t.lines || []).forEach(l => {
           flat.push({
              ...t,
              productId: l.productId,
              variantId: l.variantId,
              qty: l.qty
           });
        });
     });
     return flat.sort((a,b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [emergencyFulfillments]);

  return (
    <div className="bg-white border border-[var(--theme-tertiary)] rounded-lg p-5 shadow-sm overflow-hidden mr-2 h-full flex flex-col">
       {flattenedFulfillments.length === 0 ? (
          <p className="text-sm text-slate-400 font-semibold italic text-center py-12">No fulfillment records found.</p>
       ) : (
          <div className="flex-1 min-h-0 overflow-auto custom-scrollbar table-scrollbar -mx-5 -mb-5 mt-0">
             <table className="w-full text-left border-collapse min-w-[800px]">
              <thead className="bg-slate-50 sticky top-0 z-10">
                <tr className="border-b border-[var(--theme-tertiary)]">
                  <th className="py-4 pl-6 pr-3 font-extrabold text-[10px] uppercase tracking-widest text-slate-500">Date &amp; Time</th>
                  <th className="py-4 px-3 font-extrabold text-[10px] uppercase tracking-widest text-slate-500">From (Requester)</th>
                  <th className="py-4 px-3 font-extrabold text-[10px] uppercase tracking-widest text-slate-500">To (Provider)</th>
                  <th className="py-4 px-3 font-extrabold text-[10px] uppercase tracking-widest text-slate-500">Product</th>
                  <th className="py-4 px-3 font-extrabold text-[10px] uppercase tracking-widest text-slate-500">Variant</th>
                  <th className="py-4 pr-6 pl-3 font-extrabold text-[10px] uppercase tracking-widest text-slate-500 text-center">Qty</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--theme-tertiary)] text-sm">
                {flattenedFulfillments.map((item, i) => {
                  const prod = productById[item.productId];
                  const varLabel = (prod?.variants || []).find(v => v.id === item.variantId)?.label || 'Standard';
                  return (
                  <tr key={i} className="hover:bg-slate-50/50 transition-colors">
                    <td className="py-4 pl-6 pr-3 text-slate-500 font-medium text-xs whitespace-nowrap">{new Date(item.createdAt).toLocaleString()}</td>
                    <td className="py-4 px-3 font-bold text-[#0d0d0d] text-xs">{locationLabelOf(item.toLocationId)}</td>
                    <td className="py-4 px-3 font-bold text-[#0d0d0d] text-xs">{locationLabelOf(item.fromLocationId)}</td>
                    <td className="py-4 px-3 font-bold text-slate-700 text-xs">{prod?.name || item.productId}</td>
                    <td className="py-4 px-3 text-slate-500 text-xs">{varLabel}</td>
                    <td className="py-4 pr-6 pl-3 font-bold text-[#0d0d0d] text-xs text-center">{item.qty}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
       )}
    </div>
  );
};
